import React, { useState } from 'react';

import { hasValue, nextContinuousAngle, type MaybeNumber } from '../utils';

// The needle angle for a wind direction, kept continuous across updates so CSS transitions take the short way.
function useContinuousAngle(deg: MaybeNumber) {
    const [needle, setNeedle] = useState<{ deg: number; angle: number } | null>(null);

    if (hasValue(deg) && deg !== needle?.deg) {
        // Derived from the previous render's state, set during render: React's pattern for adjusting state
        // when a prop changes. Unlike a ref, a discarded render leaves no trace.
        const next = { deg, angle: nextContinuousAngle(needle?.angle ?? null, deg) };
        setNeedle(next);
        return next.angle;
    }

    return needle?.angle ?? 0;
}

interface CompassProps {
    deg: number | null;
    labels: string[]; // N, E, S, W
    cardinal: string;
    label: string; // what screen readers announce (the drawing itself is not read out)
}

const Compass: React.FC<CompassProps> = ({ deg, labels, cardinal, label }) => {
    const angle = useContinuousAngle(deg);
    const hasDir = hasValue(deg);

    return (
        <svg className="compass" viewBox="0 0 100 100" role="img" aria-label={label}>
            <circle cx="50" cy="50" r="47" className="compass-ring" />
            <g className="compass-ticks">
                {Array.from({ length: 36 }, (_, i) => (
                    <line key={i} x1="50" y1="5" x2="50" y2={i % 9 === 0 ? 12 : 9}
                        transform={`rotate(${i * 10} 50 50)`} />
                ))}
            </g>
            <text x="50" y="23" className="compass-letter north">{labels[0]}</text>
            <text x="80" y="53.5" className="compass-letter">{labels[1]}</text>
            <text x="50" y="84" className="compass-letter">{labels[2]}</text>
            <text x="20" y="53.5" className="compass-letter">{labels[3]}</text>
            {hasDir && (
                <g className="compass-needle-swing">
                    <g className="compass-needle" style={{ transform: `rotate(${angle}deg)` }}>
                        <path d="M50 2 L57 16 L50 13 L43 16 Z" />
                    </g>
                </g>
            )}
            <text x="50" y="52" className="compass-cardinal">{cardinal}</text>
            <text x="50" y="64" className="compass-deg">{hasDir ? `${Math.round(deg)}°` : ''}</text>
        </svg>
    );
};

export default Compass;
